// apps/web/lib/features/documents/client/signing-fragment-token.ts
//
// Where the token of a fragment signing link (/sign/w9#token=<token>,
// /sign/1099#token=<token>) lives between the address bar and the page.
//
// instrumentation-client.ts calls takeSigningFragmentEarly() before PostHog
// and OpenTelemetry start: it moves the token into this tab's sessionStorage,
// stamped with this page load, and takes it out of the address bar — so the
// first pageview, the document-load span and PostHog's stored
// $initial_person_info never see it. The page then reads the stamped token
// (use-fragment-signing-token.ts). A token stamped by an earlier load is
// honoured only on a reload or a back/forward visit, so a stale token never
// opens for a fresh visit to the bare page. Nothing here is ever sent
// anywhere. Plain DOM: no React.

import { tokenFromSigningFragment } from "../shared/signing-link-format";
import type { SigningPageKind } from "../shared/signing-link-format";

interface KeptToken {
  token: string;
  /** The page load that stored it: its performance time origin. */
  load: string;
}

/** The signing page a path is, when it is one of the two fragment pages. */
function signingPageForPath(pathname: string): SigningPageKind | null {
  const match = /^\/sign\/(w9|1099)\/?$/.exec(pathname);
  return match ? (match[1] as SigningPageKind) : null;
}

function storageKey(page: SigningPageKind): string {
  return `atriam:signing-link:${page}`;
}

/** This page load's identity, or null on a browser that can't tell. */
function currentLoad(): string | null {
  try {
    const origin = performance.timeOrigin as number | undefined;
    if (typeof origin === "number" && origin > 0) return String(origin);
    // The pre-Level-2 twin, for browsers without timeOrigin.
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- read only where timeOrigin is missing
    const timing = performance.timing as PerformanceTiming | undefined;
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- same legacy object
    const start = timing?.navigationStart;
    return typeof start === "number" && start > 0 ? String(start) : null;
  } catch {
    return null;
  }
}

/**
 * Whether this document was reached by a reload or a back/forward visit —
 * the only visits that may reopen a token an earlier load kept. A fresh
 * navigation, or a browser that can't tell, gets none.
 */
function returnVisit(): boolean {
  try {
    const entry = (
      performance.getEntriesByType(
        "navigation",
      ) as PerformanceNavigationTiming[]
    ).at(0);
    if (entry !== undefined) {
      return entry.type === "reload" || entry.type === "back_forward";
    }
    // The pre-Level-2 twin (1 reload, 2 back_forward), for browsers without
    // navigation entries.
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- read only where the Level 2 entry is missing
    const legacy = performance.navigation as PerformanceNavigation | undefined;
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- same legacy object
    const type = legacy?.type;
    return type === 1 || type === 2;
  } catch {
    return false;
  }
}

function readKept(page: SigningPageKind): KeptToken | null {
  try {
    const raw = sessionStorage.getItem(storageKey(page));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { token, load } = parsed as Partial<KeptToken>;
    return typeof token === "string" && token && typeof load === "string"
      ? { token, load }
      : null;
  } catch {
    return null;
  }
}

/**
 * Keeps `token` for this tab, stamped with this load. False when the browser
 * won't store it (blocked storage) or can't identify the load.
 */
export function keepSigningToken(
  page: SigningPageKind,
  token: string,
): boolean {
  const load = currentLoad();
  if (load === null) return false;
  try {
    sessionStorage.setItem(
      storageKey(page),
      JSON.stringify({ token, load } satisfies KeptToken),
    );
  } catch {
    return false;
  }
  return readKept(page)?.token === token;
}

/**
 * The token this page may use from storage: one this very load stored
 * (handed over by instrumentation-client, or kept by the page), or — on a
 * reload or a back/forward visit only — one an earlier load of the tab kept.
 */
export function keptSigningToken(page: SigningPageKind): string | null {
  const kept = readKept(page);
  if (kept === null) return null;
  const load = currentLoad();
  if (load !== null && kept.load === load) return kept.token;
  return returnVisit() ? kept.token : null;
}

/** Drops the tab's token for `page`: the link is dead, or done with. */
export function forgetSigningToken(page: SigningPageKind): void {
  try {
    sessionStorage.removeItem(storageKey(page));
  } catch {
    // Storage blocked: there is nothing stored to forget.
  }
}

/**
 * Takes the `#token=` fragment out of the address bar, keeping path and
 * query. The state is `null` on purpose: after hydration, Next.js's patched
 * replaceState copies its own state in and adopts the new URL only for a
 * call that doesn't carry its `__NA` marker — passing that state through
 * would leave the router's URL, fragment and all, to be written back on its
 * next update. Before hydration there is no router state to keep.
 */
export function dropSigningFragment(): void {
  if (tokenFromSigningFragment(window.location.hash) === null) return;
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}`,
  );
}

/**
 * Run from instrumentation-client.ts, before anything records location.href.
 * On a fragment signing page, moves the token into storage for the page and
 * strips it from the address bar — only once storage provably holds it; when
 * it can't, the fragment stays for the page to read itself, and the telemetry
 * redaction still keeps it out of every event. Never throws.
 */
export function takeSigningFragmentEarly(): void {
  try {
    const page = signingPageForPath(window.location.pathname);
    if (page === null) return;
    const token = tokenFromSigningFragment(window.location.hash);
    if (token === null) return;
    if (!keepSigningToken(page, token)) return;
    dropSigningFragment();
  } catch {
    // A page that can't be read this early reads its own fragment later.
  }
}
